/**
 * Centralized status style definitions
 *
 * These styles are used across components for consistent status/urgency level styling.
 * All styles include both light and dark mode variants.
 */

import { Status } from '../../../js/api';
import { StatusCategoryCodes, StatusCategoryKinds, StatusCodes } from '../constants/workflows';

// Pill Styles
export const draftCategoryKindStyle: string =
  'tw:bg-rose-50 tw:text-rose-800/80 tw:border-rose-500/30';
export const initialCategoryKindStyle: string =
  'tw:bg-blue-50 tw:text-blue-800 tw:border-blue-500/40';
export const transitoryCategoryKindStyle: string =
  'tw:bg-brand-green-2/50 tw:text-brand-green-0 tw:border-brand-green-1/50';
export const positiveFinalCategoryKindStyle: string =
  'tw:bg-green-100 tw:text-green-800 tw:border-green-500/20';
export const negativeFinalCategoryKindStyle: string =
  'tw:bg-red-100 tw:text-red-800 tw:border-red-300';
export const neutralFinalCategoryKindStyle: string =
  'tw:bg-zinc-100 tw:text-zinc-800 tw:border-zinc-300';

// Text Styles
export const neutralTextStyle = 'tw:text-zinc-600';
export const dangerTextStyle = 'tw:text-stoplight-danger';
export const successTextStyle = 'tw:text-brand-green-0';

// Default Styles
export const defaultStatusStyle = neutralFinalCategoryKindStyle;
export const defaultTextStyle = neutralTextStyle;

/**
 * Style definition for a status “bucket”.
 * - pill: classes for the pill container (bg/border/text, etc.)
 * - text: optional classes for *external text* (e.g., in a table cell) if you want to color text
 */
/** Button color prop values for status transition buttons */
export type StatusButtonColor = 'brandgreen1' | 'stoplight-danger-outline';

export const defaultButtonStyle: StatusButtonColor = 'brandgreen1';
const stoplightDangerOutline: StatusButtonColor = 'stoplight-danger-outline';

/**
 * Style definition for a status "bucket".
 * - pill: classes for the pill container (bg/border/text, etc.)
 * - text: optional classes for *external text* (e.g., in a table cell) if you want to color text
 * - button: optional Button color prop for status transition buttons
 */
export type StatusStyleDef = {
  pill?: string;
  text?: string;
  button?: StatusButtonColor;
};

/**
 * Status KIND styles
 * D: Draft (yellow)
 * I: Initial (blue)
 * T: Transitory (orange)
 */
export const CATEGORY_KIND_STYLES: Record<string, StatusStyleDef> = {
  [StatusCategoryKinds.DRAFT]: { pill: draftCategoryKindStyle, button: 'brandgreen1' },
  [StatusCategoryKinds.INITIAL]: { pill: initialCategoryKindStyle, button: 'brandgreen1' },
  [StatusCategoryKinds.TRANSITORY]: { pill: transitoryCategoryKindStyle, button: 'brandgreen1' },
  [StatusCategoryKinds.FINAL]: { pill: defaultStatusStyle, button: 'stoplight-danger-outline' },
};

/**
 * Styles defined by category
 */
export const CATEGORY_STYLES: Record<string, StatusStyleDef> = {
  [StatusCategoryCodes.CMPLT]: {
    pill: positiveFinalCategoryKindStyle,
    text: successTextStyle,
    button: 'brandgreen1',
  },
  [StatusCategoryCodes.APPROVED]: {
    pill: positiveFinalCategoryKindStyle,
    text: successTextStyle,
    button: 'brandgreen1',
  },
  [StatusCategoryCodes.REJECTED]: {
    pill: negativeFinalCategoryKindStyle,
    text: dangerTextStyle,
    button: stoplightDangerOutline,
  },
  [StatusCategoryCodes.ERROR]: {
    pill: negativeFinalCategoryKindStyle,
    text: dangerTextStyle,
    button: stoplightDangerOutline,
  },
  [StatusCategoryCodes.DENIED]: {
    pill: negativeFinalCategoryKindStyle,
    text: dangerTextStyle,
    button: stoplightDangerOutline,
  },
  [StatusCategoryCodes.CXLD]: {
    pill: negativeFinalCategoryKindStyle,
    text: dangerTextStyle,
    button: stoplightDangerOutline,
  },
  [StatusCategoryCodes.CLOSED]: {
    pill: neutralFinalCategoryKindStyle,
    text: neutralTextStyle,
    button: stoplightDangerOutline,
  },
  [StatusCategoryCodes.TERM]: {
    pill: negativeFinalCategoryKindStyle,
    text: dangerTextStyle,
    button: stoplightDangerOutline,
  },
};

/**
 * Add any specific styles for a given status, this will be global across scope
 */
export const STATUS_STYLES: Record<string, StatusStyleDef> = {
  [StatusCodes.PROV_ACTION_REQ]: {
    pill: negativeFinalCategoryKindStyle,
    text: dangerTextStyle,
    button: stoplightDangerOutline,
  },
};

/**
 * Resolve the *style definition* using fallback priority:
 * 1) Exact status code match
 * 2) Category match
 * 3) Category kind match
 * 4) Defaults
 */
export function getStatusStyleDef(status: Status): Required<StatusStyleDef> {
  const match = STATUS_STYLES[status.code] ||
    CATEGORY_STYLES[status.category] ||
    CATEGORY_KIND_STYLES[status.category_kind] || {
      pill: null,
      text: null,
      button: null,
    };

  return {
    pill: match?.pill ?? defaultStatusStyle,
    text: match?.text ?? defaultTextStyle,
    button: (match?.button ?? defaultButtonStyle) as StatusButtonColor,
  };
}

/**
 * Get Button color for status transition buttons using the same fallback chain:
 * 1) Exact status code match (STATUS_STYLES)
 * 2) Category match (CATEGORY_STYLES)
 * 3) Category kind match (CATEGORY_KIND_STYLES)
 * 4) Default
 */
export function getStatusButtonStyle(status: {
  code?: string;
  category?: string;
  category_kind?: string;
}): StatusButtonColor {
  const match =
    (status.code ? STATUS_STYLES[status.code] : undefined) ||
    (status.category ? CATEGORY_STYLES[status.category] : undefined) ||
    (status.category_kind ? CATEGORY_KIND_STYLES[status.category_kind] : undefined);

  return (match?.button ?? defaultButtonStyle) as StatusButtonColor;
}

/**
 * Back-compat: existing callers keep working (pill classes only)
 */
export function getStatusStyle(status: Status): string {
  return getStatusStyleDef(status).pill;
}

/**
 * New: get *external text* classes using the same fallback chain
 */
export function getStatusTextStyle(status: Status): string {
  return getStatusStyleDef(status).text;
}
